import { useEffect, useRef, useState } from 'react';
import classNames from 'classnames';
import dayjs from 'dayjs';
import DatePicker from 'react-datepicker';
import ClickAwayListener from '@mui/material/ClickAwayListener';
import Grow from '@mui/material/Grow';
import Paper from '@mui/material/Paper';
import Popper from '@mui/material/Popper';
import MenuItem from '@mui/material/MenuItem';
import MenuList from '@mui/material/MenuList';

import Button from '../Button';
import Input from '../Input';

import { predefinedList } from './DateRangePicker.helpers';
import * as Styles from './DateTimePicker.styles';

export const DateRangePicker = ({
  className,
  name,
  placeholder,
  dateFormat = 'dd/MM/yyyy',
  dateFormatDisplay = 'DD/MM/YYYY',
  defaultStartDate = new Date(dayjs().subtract(180, 'day')),
  defaultEndDate = new Date(),
  onChange,
  maxDate,
}) => {
  const [open, setOpen] = useState(false);
  const anchorRef = useRef(null);
  const [startDate, setStartDate] = useState(defaultStartDate);
  const [endDate, setEndDate] = useState(defaultEndDate);
  const [predefined, setPredefined] = useState('custom');
  const [inputValue, setInputValue] = useState('');

  useEffect(() => {
    if (!isNaN(defaultStartDate) && isNaN(startDate)) {
      setStartDate(defaultStartDate);
    }
    if (!isNaN(defaultEndDate) && isNaN(endDate)) {
      setEndDate(defaultEndDate);
    }
    setInputValue(getDateDisplay());
  }, [defaultStartDate, defaultEndDate]);

  const onDateChange = (dates) => {
    const [start, end] = dates;
    setStartDate(start);
    setEndDate(end);
  };

  const handleToggle = () => {
    setOpen((prevOpen) => !prevOpen);
  };

  const handleClose = (event) => {
    if (anchorRef.current && anchorRef.current.contains(event.target)) {
      return;
    }
    setOpen(false);
  };

  const prevOpen = useRef(open);
  useEffect(() => {
    if (prevOpen.current === true && open === false) {
      anchorRef.current.focus();
    }

    prevOpen.current = open;
  }, [open]);

  const option = {
    selected: startDate,
    onChange: onDateChange,
    startDate,
    endDate,
    selectsRange: true,
    inline: true,
    monthsShown: 2,
    dateFormat,
    shouldCloseOnSelect: false,
  };

  if (maxDate) {
    option.maxDate = maxDate;
  }

  const handleSelectDate = () => {
    if (onChange) {
      onChange(startDate, endDate);
    }
    setOpen(false);
    setInputValue(getDateDisplay());
  };

  const handlePredefinedClick = (value) => {
    setPredefined(value);
    if (value !== 'all') {
      setStartDate(new Date(dayjs().subtract(Number(value), 'day')));
      setEndDate(new Date());
    } else {
      setStartDate(new Date());
      setEndDate(null);
    }
  };

  const getDateDisplay = () => {
    if (!endDate) {
      return dayjs(startDate).format(dateFormatDisplay);
    }
    if (dayjs(startDate).valueOf() < dayjs(endDate).valueOf()) {
      return `${dayjs(startDate).format(dateFormatDisplay)} - ${dayjs(
        endDate
      ).format(dateFormatDisplay)}`;
    }

    return `${dayjs(endDate).format(dateFormatDisplay)} - ${dayjs(
      startDate
    ).format(dateFormatDisplay)}`;
  };

  return (
    <Styles.DateRangePickerWrapper
      className={classNames('relative inline-flex', className)}
    >
      <Styles.Button
        ref={anchorRef}
        id="composition-button"
        aria-controls={open ? 'composition-date-range-picker' : undefined}
        aria-expanded={open ? 'true' : undefined}
        aria-haspopup="true"
        onClick={handleToggle}
        className={open ? 'opened' : ''}
      >
        <Input
          name={name || 'dateRangePicker'}
          placeholder={placeholder}
          defaultValue={inputValue}
          forceUpdate
        />
      </Styles.Button>
      <Popper
        open={open}
        anchorEl={anchorRef.current}
        role={undefined}
        placement="bottom-start"
        transition
        disablePortal
      >
        {({ TransitionProps, placement }) => (
          <Grow
            {...TransitionProps}
            style={{
              transformOrigin:
                placement === 'bottom-start' ? 'left top' : 'left bottom',
            }}
          >
            <Paper>
              <ClickAwayListener onClickAway={handleClose}>
                <Styles.Wrapper className="date-range-picker">
                  <div className="flex">
                    <div className="pr-6 border-r border-gray-150">
                      <MenuList>
                        {predefinedList.map((item, index) => (
                          <MenuItem
                            key={`${item.value}-${index}`}
                            onClick={() => handlePredefinedClick(item.value)}
                            className={classNames(
                              'text-sm',
                              predefined === item.value ? 'active' : ''
                            )}
                          >
                            {item.name}
                          </MenuItem>
                        ))}
                      </MenuList>
                    </div>
                    <div>
                      <DatePicker {...option} />
                    </div>
                  </div>
                  <div className="footer items-center">
                    <span className="inline-flex mr-3 text-sm">
                      {getDateDisplay()}
                    </span>
                    <Button onClick={handleToggle} variant="outlined">
                      Hủy
                    </Button>
                    <Button onClick={handleSelectDate}>Chọn</Button>
                  </div>
                </Styles.Wrapper>
              </ClickAwayListener>
            </Paper>
          </Grow>
        )}
      </Popper>
    </Styles.DateRangePickerWrapper>
  );
};

const DateTimePicker = ({
  placeholder,
  onChange,
  defaultDate = new Date(),
  dateFormat = 'dd/MM/yyyy h:mm aa',
  formik,
  name,
  maxDate,
  dateFormatDisplay = 'DD/MM/YYYY HH:mm',
  showTimeInput = true,
  className = '',
  backgroundColor = '',
  minDate = null,
  hideWeekday,
  isDetailPage = false,
  ...rest
}) => {
  const dateTimePickerRef = useRef(null);
  const [startDate, setStartDate] = useState(defaultDate);

  useEffect(() => {
    if (isDetailPage || (!isDetailPage && !startDate)) {
      setStartDate(defaultDate);
    }
  }, [defaultDate]);

  const handleShowDatePicker = () => {
    dateTimePickerRef.current.setOpen(false);
  };

  const handleOnChange = (date) => {
    setStartDate(date);
  };

  const handleSelectDate = () => {
    if (formik) {
      formik.handleChange({
        target: {
          name,
          value: dayjs(startDate).format(dateFormatDisplay),
        },
      });
    }
    if (onChange) {
      onChange(startDate);
    }
    dateTimePickerRef.current.setOpen(false);
  };

  const options = {
    ...rest,
    selected: startDate,
    onChange: (date) => handleOnChange(date),
    timeInputLabel: '',
    showTimeInput,
    dateFormat,
    showMonthDropdown: true,
    showYearDropdown: true,
    shouldCloseOnSelect: false,
    dropdownMode: 'select',
    ref: dateTimePickerRef,
  };

  if (minDate) {
    options.minDate = minDate;
  }

  if (maxDate) {
    options.maxDate = maxDate;
  }

  const isWeekday = (date) => {
    const day = dayjs(date).get('day');
    return day !== 0 && day !== 6;
  };

  if (hideWeekday) {
    options.filterDate = isWeekday;
  }

  return (
    <Styles.Wrapper className={classNames(className, backgroundColor)}>
      <DatePicker {...options}>
        <div className="footer">
          <Button
            onClick={handleShowDatePicker}
            variant="outlined"
            className="mr-3 view-detail"
          >
            Hủy
          </Button>
          <Button onClick={handleSelectDate}>Chọn</Button>
        </div>
      </DatePicker>
    </Styles.Wrapper>
  );
};

export default DateTimePicker;
